import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Stack, HStack, Badge, Icon, Spinner, Button } from "@chakra-ui/react";
import { ShieldCheck, Building2, ChevronRight, WifiOff, CloudUpload, CheckCircle2, ClipboardList, Pencil, Flag, GripVertical, ChevronUp, ChevronDown, Archive, ListOrdered, ArchiveRestore } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { getOutbox, flushOutbox, cachePut, cacheGet, safeAll } from "@/lib/offline";
import { countFields } from "@/lib/templateFields";
import { statusMeta } from "@/lib/submissionMeta";
import { displayStatus } from "@/lib/urgent";

import { colors } from "@/theme/colors";
import { logWarn } from "@/lib/log";

export default function Forms() {
  const { user, role, activeCompanyId, companyName, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [managing, setManaging] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [mySubs, setMySubs] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  const [justSent, setJustSent] = useState(0);
  const isAdmin = role === "org_admin";

  const load = useCallback(async () => {
    // Tenant isolation (Sweep A2): both lists pinned to the ACTING company —
    // an unscoped "my submissions" list exposed a cross-company Revise path.
    const tplQ = supabase.from("templates").select("*").eq("status", "published").order("created_at");
    const subQ = supabase.from("submissions").select("id,template_id,template_title,template_type,location_name,status,created_at,flagged,revision,revision_of")
      .eq("submitted_by", user.id).order("created_at", { ascending: false }).limit(10);
    const [tplRes, subRes, listRes] = await safeAll([
      activeCompanyId ? tplQ.or(`company_id.is.null,company_id.eq.${activeCompanyId}`) : tplQ.is("company_id", null),
      activeCompanyId ? subQ.eq("company_id", activeCompanyId) : subQ,
      activeCompanyId
        ? supabase.from("company_form_list").select("template_id,position,retired").eq("company_id", activeCompanyId)
        : Promise.resolve({ data: [] }),
    ]);
    if (tplRes.error) {
      // Offline: serve the last-known-good picker so new forms stay startable.
      const snap = cacheGet(user.id, "forms") || {};
      const homeSnap = cacheGet(user.id, "home") || {};
      const tpls = snap.templates || homeSnap.templates || [];
      setTemplates(tpls);
      setMySubs(snap.mySubs || homeSnap.mySubs || []);
      setPrefs(snap.prefs || {});
      // The load FAILED — we only know we couldn't check, not that lists are empty.
      setLoadFailed(true);
    } else {
      const prefs = Object.fromEntries((listRes.data || []).map((r) => [r.template_id, r]));
      setTemplates(tplRes.data || []);
      setMySubs(subRes.data || []);
      setPrefs(prefs);
      setLoadFailed(false);
      cachePut(user.id, "forms", { templates: tplRes.data || [], mySubs: subRes.data || [], prefs });
    }
    setLoading(false);
  }, [user.id, activeCompanyId]);

  const tryFlush = useCallback(async () => {
    setQueued((await getOutbox(user.id)).length);
    if (!navigator.onLine) return;
    const { sent, remaining } = await flushOutbox(supabase, user.id);
    setQueued(remaining);
    if (sent > 0) { setJustSent(sent); load(); setTimeout(() => setJustSent(0), 5000); }
  }, [user.id, load]);

  useEffect(() => {
    load();
    tryFlush();
    const on = () => { setOnline(true); tryFlush(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [load, tryFlush]);

  // Curated ordering: rows with an admin-set position first, then new
  // (unlisted) forms auto-appended at the end of their group by created_at.
  const sortGroup = (list) =>
    [...list].sort((a, b) => {
      const pa = prefs[a.id]?.position ?? Infinity;
      const pb = prefs[b.id]?.position ?? Infinity;
      if (pa !== pb) return pa - pb;
      return new Date(a.created_at) - new Date(b.created_at);
    });
  const isRetired = (t) => Boolean(prefs[t.id]?.retired);

  const masters = sortGroup(templates.filter((t) => t.company_id === null));
  const mine = sortGroup(templates.filter((t) => t.company_id === activeCompanyId));
  const activeMasters = masters.filter((t) => !isRetired(t));
  const activeMine = mine.filter((t) => !isRetired(t));
  const retiredForms = [...mine, ...masters].filter(isRetired);

  // Persist one group's active order + a template's retired flag. RLS allows
  // writes only for this company's Org Admin; a DB trigger audits every change.
  const saveGroup = async (groupActive, extraRows = []) => {
    const rows = groupActive.map((t, i) => ({
      company_id: activeCompanyId, template_id: t.id, position: (i + 1) * 10,
      retired: false, updated_by: user.id,
    })).concat(extraRows.map((r) => ({ ...r, company_id: activeCompanyId, updated_by: user.id })));
    const { error } = await supabase.from("company_form_list").upsert(rows);
    if (error) logWarn("[forms] save list failed:", error.message);
    await load();
  };

  const groupOf = (t) => (t.company_id === null ? activeMasters : activeMine);

  const move = (t, dir) => {
    const group = [...groupOf(t)];
    const i = group.findIndex((x) => x.id === t.id);
    const j = i + dir;
    if (j < 0 || j >= group.length) return;
    [group[i], group[j]] = [group[j], group[i]];
    saveGroup(group);
  };


  // Pointer-based drag (works with mouse AND touch — HTML5 DnD does not).
  const rowAt = (x, y) => document.elementFromPoint(x, y)?.closest("[data-row-id]")?.getAttribute("data-row-id") || null;

  const startDrag = (e, t) => {
    e.preventDefault();
    setDragId(t.id);
    const onMove = (ev) => setOverId(rowAt(ev.clientX, ev.clientY));
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const targetId = rowAt(ev.clientX, ev.clientY);
      setOverId(null);
      setDragId(null);
      if (!targetId || targetId === t.id) return;
      const group = [...groupOf(t)];
      const from = group.findIndex((x) => x.id === t.id);
      const to = group.findIndex((x) => x.id === targetId);
      if (from === -1 || to === -1) return;
      const [moved] = group.splice(from, 1);
      group.splice(to, 0, moved);
      saveGroup(group);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const retire = (t) => {
    const group = groupOf(t).filter((x) => x.id !== t.id);
    saveGroup(group, [{ template_id: t.id, position: prefs[t.id]?.position ?? 1000000, retired: true }]);
  };

  const recall = (t) => {
    const group = t.company_id === null ? activeMasters : activeMine;
    saveGroup([...group, t]);
  };

  const TemplateRow = ({ t, master }) => (
    <Flex align="center" gap="2" data-testid={`form-row-${t.id}`} data-row-id={t.id}
      opacity={dragId === t.id ? 0.45 : 1}
      outline={overId === t.id && dragId && dragId !== t.id ? `2px solid ${colors.teal}` : "none"}
      borderRadius="12px" transition="opacity 0.1s ease">
      {managing && (
        <Icon as={GripVertical} boxSize="5" color={colors.textSecondary} cursor="grab" flexShrink={0}
          touchAction="none" onPointerDown={(e) => startDrag(e, t)}
          data-testid={`drag-handle-${t.id}`} />
      )}
      <Flex as="button" type="button" onClick={() => !managing && navigate(`/forms/fill/${t.id}`)} data-testid={`form-card-${t.id}`}
        w="full" textAlign="left" align="center" gap="3" bg={colors.white} border="1px solid" borderColor={colors.border}
        borderRadius="12px" px="4" py="3.5" _hover={managing ? {} : { borderColor: colors.teal, bg: colors.tealTint }}
        cursor={managing ? "default" : "pointer"}
        transition="border-color 0.15s ease, background-color 0.15s ease">
        <Icon as={master ? ShieldCheck : Building2} boxSize="5" color={master ? colors.teal : colors.sageDeep} flexShrink={0} />
        <Box flex="1">
          <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{t.title}</Text>
          <Text fontSize="xs" color={colors.textSecondary}>{t.type} · {countFields(t.schema)} questions</Text>
        </Box>
        {!managing && <Icon as={ChevronRight} boxSize="5" color={colors.textSecondary} />}
      </Flex>
      {managing && (
        <HStack gap="0.5" flexShrink={0}>
          <Button size="xs" variant="ghost" px="8px" minH="34px" onClick={() => move(t, -1)} data-testid={`move-up-${t.id}`} aria-label="Move up"
            disabled={groupOf(t).findIndex((x) => x.id === t.id) === 0}>
            <ChevronUp size={15} />
          </Button>
          <Button size="xs" variant="ghost" px="8px" minH="34px" onClick={() => move(t, 1)} data-testid={`move-down-${t.id}`} aria-label="Move down"
            disabled={groupOf(t).findIndex((x) => x.id === t.id) === groupOf(t).length - 1}>
            <ChevronDown size={15} />
          </Button>
          <Button size="xs" variant="ghost" px="8px" minH="34px" color={colors.poppy} onClick={() => retire(t)} data-testid={`retire-${t.id}`} aria-label="Retire">
            <Archive size={15} />
          </Button>
        </HStack>
      )}
    </Flex>
  );

  return (
    <Box data-testid="forms-page" maxW="640px">
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <Box>
          <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Field Forms</Heading>
          <Text color={colors.textSecondary} mb="5">
            {managing ? "Drag or use the arrows to reorder. Retire a form to hide it from the field team." : `Pick a form to fill out${companyName ? ` for ${companyName}` : ""}.`}
          </Text>
        </Box>
        {isAdmin && (
          managing ? (
            <Button size="sm" onClick={() => setManaging(false)} data-testid="manage-done-btn">Done</Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setManaging(true)} data-testid="manage-list-btn">
              <ListOrdered size={15} /> Manage list
            </Button>
          )
        )}
      </Flex>

      {!online && (
        <HStack mb="4" px="4" py="2.5" borderRadius="10px" bg="#FBEEDA" color="#8A5106" data-testid="offline-banner">
          <Icon as={WifiOff} boxSize="4" />
          <Text fontSize="sm">
            {templates.length > 0
              ? "You're offline. You can still fill the forms saved on this device — they'll send automatically when you're back online."
              : "You're offline and this device hasn't loaded your forms yet. Reconnect once to load them, then they'll be available offline."}
          </Text>
        </HStack>
      )}
      {queued > 0 && (
        <HStack mb="4" px="4" py="2.5" borderRadius="10px" bg={colors.tealTint} color={colors.tealHover} data-testid="queued-banner">
          <Icon as={CloudUpload} boxSize="4" />
          <Text fontSize="sm">{queued} submission{queued === 1 ? "" : "s"} waiting to send — will go automatically once you're online.</Text>
        </HStack>
      )}
      {justSent > 0 && (
        <HStack mb="4" px="4" py="2.5" borderRadius="10px" bg={colors.resolvedBg} color={colors.resolvedText} data-testid="sent-banner">
          <Icon as={CheckCircle2} boxSize="4" />
          <Text fontSize="sm">{justSent} queued submission{justSent === 1 ? "" : "s"} sent successfully.</Text>
        </HStack>
      )}

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : isSuperAdmin ? (
        <Text color={colors.textSecondary} fontSize="sm">Form filling is for company members. Use Form Templates to manage masters.</Text>
      ) : (
        <Stack gap="6">
          {activeMine.length > 0 && (
            <Box>
              <Text fontWeight="700" fontSize="sm" mb="2" color={colors.textPrimary}>{companyName} forms</Text>
              <Stack gap="2">{activeMine.map((t) => <TemplateRow key={t.id} t={t} />)}</Stack>
            </Box>
          )}
          <Box>
            <Text fontWeight="700" fontSize="sm" mb="2" color={colors.textPrimary}>Hakina standard forms</Text>
            {activeMasters.length === 0 ? (
              <Text fontSize="sm" color={colors.textSecondary}>
                {navigator.onLine
                  ? "No published forms available yet."
                  : "You're offline — forms will appear once this device has loaded them online."}
              </Text>
            ) : (
              <Stack gap="2">{activeMasters.map((t) => <TemplateRow key={t.id} t={t} master />)}</Stack>
            )}
          </Box>

          {isAdmin && retiredForms.length > 0 && (
            <Box data-testid="retired-section">
              <Button size="xs" variant="ghost" onClick={() => setShowRetired((v) => !v)} data-testid="retired-section-toggle">
                <Archive size={14} /> Retired forms ({retiredForms.length}) {showRetired ? "▾" : "▸"}
              </Button>
              {showRetired && (
                <Stack gap="2" mt="2">
                  {retiredForms.map((t) => (
                    <Flex key={t.id} align="center" gap="3" bg={colors.cream} border="1px dashed" borderColor={colors.border}
                      borderRadius="12px" px="4" py="3" data-testid={`retired-row-${t.id}`}>
                      <Icon as={t.company_id === null ? ShieldCheck : Building2} boxSize="5" color={colors.textSecondary} flexShrink={0} />
                      <Box flex="1">
                        <Text fontWeight="600" fontSize="sm" color={colors.textSecondary}>{t.title}</Text>
                        <Text fontSize="xs" color={colors.textSecondary}>Hidden from the field team's browse list — can still be assigned as a task.</Text>
                      </Box>
                      <Button size="xs" variant="outline" onClick={() => recall(t)} data-testid={`recall-${t.id}`}>
                        <ArchiveRestore size={14} /> Recall
                      </Button>
                    </Flex>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          <Box data-testid="my-submissions">
            <HStack gap="2" mb="2">
              <Icon as={ClipboardList} boxSize="4" color={colors.textSecondary} />
              <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>My recent submissions</Text>
            </HStack>
            {mySubs.length === 0 ? (
              <Text fontSize="sm" color={colors.textSecondary}>
                {loadFailed
                  ? "You're offline — your recent submissions will appear once this device has loaded them."
                  : "Nothing submitted yet."}
              </Text>
            ) : (
              <Stack gap="2">
                {mySubs.map((s) => {
                  const meta = statusMeta(displayStatus(s));
                  return (
                    <Flex key={s.id} data-testid={`submission-row-${s.id}`} align="center" gap="3" flexWrap="wrap"
                      bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="10px" px="4" py="2.5">
                      <Box flex="1" minW="160px">
                        <HStack gap="1.5" flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="500" color={colors.textPrimary}>{s.template_title}</Text>
                          {s.revision > 1 && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px">Rev {s.revision}</Badge>}
                          {s.flagged && <Icon as={Flag} boxSize="3.5" color={colors.poppy} data-testid={`flagged-icon-${s.id}`} />}
                        </HStack>
                        <Text fontSize="xs" color={colors.textSecondary}>
                          {s.location_name} · {new Date(s.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                        </Text>
                      </Box>
                      <HStack gap="2">
                        <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5">{meta.label}</Badge>
                        {s.status === "pending" && (
                          <Button size="xs" variant="outline"
                            onClick={() => navigate(`/forms/fill/${s.template_id}?revise=${s.id}`)}
                            data-testid={`revise-btn-${s.id}`}>
                            <Pencil size={13} /> Revise
                          </Button>
                        )}
                      </HStack>
                    </Flex>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
